import { Metamodel } from 'app/model/base/model';
import { Column } from 'app/model/dataset/column';
import { NamedArray } from 'app/model/base/namedarray';
import { Model } from 'app/model/backendmodel';

import { Dataset } from 'app/model/dataset/dataset';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import {
    Constants,
    convertParametersToModel,
    Folder,
    NamedAsyncAdapter,
    ReferenceAdapter,
    Tag,
    WorkspaceBuildInLinkedService,
    isLogicAppExpression,
    SharedModel,
    IModel,
    ConnectionKind,
    artifactsSupportDMTSCredential,
    artifactLinkedSerivceToKindMap,
    artifactLinkedSerivceType,
    ParameterConfig,
    stringIsNullOrEmpty,
} from 'shell-models-lib';
import { convertObjectToString, getString } from 'app/shared/utils/string.util';
import { Parameter } from 'app/model/base/parameter';
import { Owned } from 'app/model/base/named';
import { linkedServiceReferenceToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { columnResourceToValue, columnValueToResource } from '../column.adapter.util';
import { IStructure } from './dataset.constant.adapter';
import { HierarchicalNode } from 'app/model/dataset/hierarchicalnode';
import _ from 'lodash';
import { GlobalVariables } from 'app/services/globals';
import { isWorkspaceBuiltInType } from 'app/model/adapters/linkedservices/linked-service-adapter.util';
import { LinkedServiceAdapter } from '../linkedservices/linkedservice.adapter';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { LoggerService } from 'app/logger/logger.service';
import { ActivityConnectionContext, IUnknownConnectionContext } from 'app/interfaces/copilot.pipeline.action.types';
import { adfModelTypeMap, resolveLinkedServiceType } from 'app/registries/consts/adf-model-type-map-const';
import { toParameterClass, toParameterObject } from '../linkedservices/parameter-config.util';
import { type IFrameService } from 'app/services/iframe.service';

export abstract class DatasetAdapter<
    T extends Dataset,
    U extends Model.DatasetResource,
    D extends ActivityConnectionContext = ActivityConnectionContext
> extends NamedAsyncAdapter<T, U, D> {
    // for each hierarchical connector, implement this to enable such schema parsable.
    public parseHierarchicalSchema(obj: unknown): HierarchicalNode {
        return undefined;
    }

    public override async toFabricCopilotContext(dataset: T): Promise<D> {
        try {
            if (dataset.linkedService) {
                const linkedServiceAdapter = this.context.asyncAdapterFor<LinkedService, Model.LinkedService, D>(
                    Metamodel.modelOf(dataset.linkedService)
                );
                const result = await linkedServiceAdapter.toFabricCopilotContext(dataset.linkedService);
                if (dataset.linkedService.getLinkedServiceType() === adfModelTypeMap.lakehouse.linkedServiceType) {
                    result.Type =
                        Metamodel.modelOf(dataset).name === adfModelTypeMap.lakehouseTable.datasetType
                            ? 'LakehouseDb'
                            : 'LakehouseFiles';
                } else if (dataset.getLinkedServiceType() !== result.Type) {
                    const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
                        this.context.contextData,
                        'LinkedServiceRegistryService'
                    );
                    const lsConfigutation = linkedServiceRegistryService.getLinkedServiceConfiguration(
                        dataset.getLinkedServiceType()
                    );
                    if (lsConfigutation) {
                        if (lsConfigutation.isKnownInPipelineContext) {
                            result.Type = lsConfigutation.type;
                        } else {
                            result.Type = 'Unknown';
                            (result as IUnknownConnectionContext).OriginalConnectionType = lsConfigutation.type;
                        }
                    }
                }
                return result;
            }
            return undefined;
        } catch (e) {
            this._logWarning(`Failed to convert dataset to Fabric context: ${convertObjectToString(e)}`);
            return undefined;
        }
    }

    public override async fromFabricCopilotContext(context: D, model: T): Promise<T> {
        if (context) {
            if (!model) {
                throw new Error('Dataset should be initialized before calling Dataset::fromFabricCopilotContext');
            }
            const linkedServiceAdapter = this.context.asyncAdapterFor<LinkedService, Model.LinkedService, D>(
                Metamodel.modelOf(LinkedService)
            );
            if (linkedServiceAdapter) {
                model.linkedService = await linkedServiceAdapter.fromFabricCopilotContext(
                    {
                        ...context,
                        Type:
                            context.Type === 'LakehouseDb' || context.Type === 'LakehouseFiles'
                                ? adfModelTypeMap.lakehouse.linkedServiceType
                                : context.Type
                    },
                    model.linkedService
                );
            }
        }
        return model;
    }

    protected async _toResource(dataset: T): Promise<U> {
        const datasetResource = (await super._toResource(dataset)) as U;
        this.properties(datasetResource).linkedServiceName = linkedServiceReferenceToResource(
            dataset.linkedService,
            this.context,
            this.ensureLogger(),
            dataset.linkedServiceParameters
        );

        const parameters: { [propertyName: string]: Model.ParameterSpecification } = {};
        const parameterAdapter = this.context.adapterFor(Parameter.MODEL);
        if (parameterAdapter && dataset.parameters != null) {
            dataset.parameters.forEach((parameter) => {
                if (parameter.name) {
                    parameters[getString(parameter.name)] = parameterAdapter.toResource(parameter);
                }
            });
        }

        if (dataset.parameters != null && dataset.parameters.size > 0) {
            this.properties(datasetResource).parameters = parameters;
        }

        this.properties(datasetResource).folder = this.context.adapterFor(Folder.MODEL).toResource(dataset.folder);
        this.properties(datasetResource).annotations = [];
        const tagAdapter = this.context.adapterFor(Tag.MODEL);
        for (const tagNamed of dataset.tags) {
            // do not save it if user created an empty tag
            const tagResource = tagAdapter.toResource(tagNamed);
            if (tagResource) {
                this.properties(datasetResource).annotations.push(tagResource);
            }
        }
        for (const tagNamed of dataset.malformedTags) {
            const tagResource = tagAdapter.toResource(tagNamed);
            if (tagResource) {
                this.properties(datasetResource).annotations.push(tagResource);
            }
        }

        // Copy activity that has an artifact source or sink will reference the linked service directly.
        if (__IS_FABRIC_DPE__ && dataset.linkedService) {
            if (dataset.linkedService.isWorkspaceBuiltIn || dataset.isSampleDataset) {
                if (datasetResource.properties) {
                    Reflect.deleteProperty(datasetResource.properties, 'linkedServiceName');
                }
                const linkedServiceAdapter = this.context.asyncAdapterFor(Metamodel.modelOf(dataset.linkedService));
                let linkedService: Model.LinkedServiceResource = await linkedServiceAdapter.toResource(
                    dataset.linkedService
                );
                if (dataset.linkedService.isWorkspaceBuiltIn && linkedService && dataset.dmtsCredentialForArtifact) {
                    linkedService.properties.linkedServiceName = linkedServiceReferenceToResource(
                        dataset.dmtsCredentialForArtifact,
                        this.context,
                        this.ensureLogger()
                    );
                }
                if (dataset.linkedService.isWorkspaceBuiltIn && _.isEmpty(linkedService.properties.typeProperties)) {
                    // when cannot find matching linked service, generate resource linked service properties from extension
                    linkedService = dataset.extension._linkedService$ as Model.LinkedServiceResource;
                }
                if (linkedService?.properties) {
                    Reflect.deleteProperty(linkedService.properties, 'tridentConnectionKind');
                    Reflect.deleteProperty(linkedService, 'objectId');
                }
                this.setArtifactLinkedService(
                    datasetResource,
                    linkedService,
                    dataset.dmtsCredentialForArtifact != null
                );
            }

            if (dataset.linkedServiceProperties?.length > 0) {
                this.properties(datasetResource).connectionProperties = {
                    ...toParameterObject(dataset.linkedServiceProperties, false, undefined, true)
                }
            }
        }

        return datasetResource;
    }

    protected async _toModel(modelDatasetResource: U, dataset: T): Promise<T> {
        if (!dataset) {
            throw new Error('Dataset should be initialized before calling Dataset::_toModel');
        }

        await super._toModel(modelDatasetResource, dataset);
        const logger = await getServiceInstance<LoggerService>(this.context.contextData, 'LoggerService');
        if (modelDatasetResource.properties && modelDatasetResource.properties.linkedServiceName) {
            if (
                __IS_FABRIC_DPE__ &&
                isLogicAppExpression(
                    (modelDatasetResource.properties.linkedServiceName as SharedModel.LinkedServiceReference)
                        .referenceName
                )
            ) {
                const linkedServiceType: string = modelDatasetResource.properties.typeProperties?.location?.type
                    ? (
                          Metamodel.modelByName(modelDatasetResource.properties.typeProperties.location.type).tag(
                              'linkedServiceModel'
                          ) as IModel
                      )?.name
                    : Metamodel.modelByName(modelDatasetResource.properties.type).attribute('linkedService').constraint
                          ?.model?.name;
                if (linkedServiceType) {
                    const lsModel = Metamodel.modelByName(linkedServiceType);
                    const templateLinkedService = Owned.create<LinkedService>(lsModel, dataset.owner);
                    templateLinkedService.name = generateUUID().replaceAll('-', '_');
                    ValueAdapter.toModel(
                        templateLinkedService,
                        Metamodel.modelByName(linkedServiceType).attribute('objectId'),
                        (modelDatasetResource.properties.linkedServiceName as SharedModel.LinkedServiceReference)
                            .referenceName
                    );
                    templateLinkedService.tridentArtifactType =
                        templateLinkedService.tridentArtifactType ?? 'Connection';
                    const iframeService = await getServiceInstance<IFrameService>(
                        this.context.contextData,
                        'IFrameService'
                    );
                    templateLinkedService.tridentConnectionKind = (iframeService.currentCustomerProfile.linkedServiceToConnectionTypeMapping[linkedServiceType] || linkedServiceType) as ConnectionKind;
                    templateLinkedService.parameterized = true;
                    dataset.linkedService = templateLinkedService;
                } else {
                    logger.log(
                        'dataset.adapter',
                        'linkedService connection id is parameterized & cannot find related linkedService type',
                        { resourceName: modelDatasetResource.name }
                    );
                }

                logger.log('dataset.adapter', 'linkedService connection id is parameterized', { linkedServiceType });
            } else {
                dataset.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                    Metamodel.modelFor(LinkedService),
                    modelDatasetResource.properties.linkedServiceName,
                    this.context
                );
            }

            if ((modelDatasetResource.properties.linkedServiceName as Model.LinkedServiceReference).parameters) {
                // Parameter value for linked service
                convertParametersToModel(
                    dataset,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    dataset.linkedService,
                    dataset.linkedServiceParameters,
                    (modelDatasetResource.properties.linkedServiceName as Model.LinkedServiceReference).parameters
                );
            }
        }

        const parameterAdapter = this.context.adapterFor(Parameter.MODEL);
        if (parameterAdapter) {
            if (modelDatasetResource.properties && modelDatasetResource.properties.parameters) {
                for (const parameterName in modelDatasetResource.properties.parameters) {
                    dataset.parameters.add(
                        parameterAdapter.toModel(
                            modelDatasetResource.properties.parameters[parameterName],
                            undefined,
                            parameterName,
                            Dataset.MODEL.name,
                            dataset.name
                        )
                    );

                    if (
                        !GlobalVariables.strictParamValidation &&
                        !Constants.alphaNumericUnderscoreValidation.test(parameterName)
                    ) {
                        this._logWarning(`Dataset "${dataset.name}" with invalid parameter "${parameterName}".`);
                    }
                }
            }
        }

        if (modelDatasetResource.properties) {
            dataset.folder = Owned.create<Folder>(Folder.MODEL, dataset);
            this.context.adapterFor(Folder.MODEL).toModel(modelDatasetResource.properties.folder, dataset.folder);
            if (modelDatasetResource.properties.annotations) {
                const tagAdapter = this.context.adapterFor(Tag.MODEL);
                dataset.tags.clear();
                dataset.malformedTags.clear();
                for (const tagNamed of modelDatasetResource.properties.annotations) {
                    if (typeof tagNamed === 'string') {
                        dataset.tags.add(tagAdapter.toModel(tagNamed));
                    } else {
                        dataset.malformedTags.add(tagAdapter.toModel(tagNamed));
                    }
                }
            }
        }

        if (__IS_FABRIC_DPE__) {
            const linkedServiceResource = this.getArtifactLinkedService(modelDatasetResource);
            const linkedServiceType = resolveLinkedServiceType(linkedServiceResource?.properties?.type);
            if (linkedServiceType) {
                const lsModel = Metamodel.modelByName(linkedServiceType);
                const lsModelAdapter = this.context.asyncAdapterFor(lsModel) as LinkedServiceAdapter<LinkedService>;

                if (linkedServiceResource.properties.linkedServiceName) {
                    const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
                        this.context.contextData,
                        'LinkedServiceRegistryService'
                    );
                    const linkedServiceMetadata =
                        linkedServiceRegistryService.getLinkedServiceConfiguration(linkedServiceType);
                    if (linkedServiceMetadata?.dmtsCredentialConnectionType) {
                        if (isLogicAppExpression(linkedServiceResource.properties.linkedServiceName.referenceName)) {
                            const model = Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType);
                            if (model) {
                                const templateLinkedService = Owned.create<LinkedService>(model, dataset.owner);
                                templateLinkedService.name = linkedServiceResource.name || generateUUID().replaceAll('-', '_');
                                ValueAdapter.toModel(
                                    templateLinkedService,
                                    Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType).attribute(
                                        'objectId'
                                    ),
                                    linkedServiceResource.properties.linkedServiceName.referenceName
                                );
                                templateLinkedService.parameterized = true;
                                dataset.dmtsCredentialForArtifact = templateLinkedService;
                            }
                            logger.log('dataset.adapter', 'artifact credential linkedService is parameterized', {
                                linkedServiceType
                            });
                        } else {
                            dataset.dmtsCredentialForArtifact = ReferenceAdapter.toProxyModel(
                                Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType),
                                linkedServiceResource.properties.linkedServiceName,
                                this.context
                            );
                        }
                    }
                }

                if (isWorkspaceBuiltInType(linkedServiceType)) {
                    const linkedServiceModel: WorkspaceBuildInLinkedService =
                        Owned.create<WorkspaceBuildInLinkedService>(
                            Metamodel.modelByName(linkedServiceType),
                            dataset.owner
                        );
                    // delete linkedServiceName to avoid it to be added in the following ls model.
                    await lsModelAdapter?.toModel(
                        {
                            ...linkedServiceResource,
                            properties: {
                                ...linkedServiceResource.properties,
                                linkedServiceName: undefined
                            }
                        },
                        linkedServiceModel
                    );
                    linkedServiceModel.name = linkedServiceResource.name || generateUUID().replaceAll('-', '_');
                    if (linkedServiceModel?.parameterized || dataset?.dmtsCredentialForArtifact?.parameterized) {
                        dataset.linkedService = linkedServiceModel;
                        if (linkedServiceModel?.parameterized) {
                            logger.log('dataset.adapter', 'linkedService is parameterized', { linkedServiceType });
                        }
                    } else {
                        // convert linkedservice resource to reference to create a proxy model in tabular/format adapter
                        modelDatasetResource.properties.linkedServiceName = {
                            type: 'LinkedServiceReference',
                            referenceName: lsModelAdapter?.getArtifactId(
                                this.getArtifactLinkedService(modelDatasetResource)
                            ),
                            workspaceObjectId: lsModelAdapter?.getWorkspaceObjectId(
                                this.getArtifactLinkedService(modelDatasetResource)
                            )
                        };
                    }
                    dataset.extension._linkedService$ = linkedServiceResource;
                } else {
                    if (lsModelAdapter != null) {
                        dataset.linkedService = await lsModelAdapter.toModel({
                            ...linkedServiceResource,
                            properties: { ...linkedServiceResource.properties, tridentArtifactType: 'SampleDataset' }
                        });
                    }
                    dataset.linkedService.tridentArtifactType = 'SampleDataset';
                    dataset.isSampleDataset = true;
                }
            }

            if (modelDatasetResource.properties.connectionProperties) {
                // remove empty values
                Object.keys(modelDatasetResource.properties.connectionProperties).forEach((key) => {
                    if (stringIsNullOrEmpty(modelDatasetResource.properties.connectionProperties[key])) {
                        delete modelDatasetResource.properties.connectionProperties[key];
                    }
                });
                dataset.linkedServiceProperties = toParameterClass(
                    modelDatasetResource.properties.connectionProperties,
                    dataset
                ) as Array<ParameterConfig<string>>;
            }
        }

        return dataset;
    }

    protected columnValueToResource(dataset: Dataset, columns: NamedArray<Column>): IStructure[] | undefined {
        return columnValueToResource(dataset, columns, this.context);
    }

    protected columnResourceToValue(dataset: Dataset, columns: IStructure[] | undefined): NamedArray<Column> {
        return columnResourceToValue(dataset, columns);
    }

    /**
     * App model support two properties as linked service, `linkedService` and `connectionSettings`, this is controlled
     * by feature flag. So always use `getArtifactLinkedService` to get the linked service.
     *
     * @param resource Dataset resource
     * @return Linked service resource
     */
    protected getArtifactLinkedService(resource: Model.DatasetResource): Model.LinkedServiceResource {
        const properties = this.properties(resource);
        if (
            !GlobalVariables.enableConnectionSettings &&
            !artifactsSupportDMTSCredential.includes(artifactLinkedSerivceToKindMap[properties.connectionSettings?.properties?.type as artifactLinkedSerivceType])
        ) {
            return properties.linkedService;
        }
        return properties.connectionSettings ?? properties.linkedService;
    }

    /**
     * For the same reason, always use `setArtifactLinkedService` to set the linked service.
     *
     * @param resource Dataset resource
     * @param linkedService Linked service resource
     */
    protected setArtifactLinkedService(
        resource: Model.DatasetResource,
        linkedService: Model.LinkedServiceResource,
        withDmtsCredential: boolean = false
    ): void {
        this.properties(resource)[
            (
                GlobalVariables.enableConnectionSettings ||
                artifactsSupportDMTSCredential.includes(artifactLinkedSerivceToKindMap[linkedService?.properties?.type as artifactLinkedSerivceType])
            ) &&
            withDmtsCredential
                ? 'connectionSettings'
                : 'linkedService'
        ] = linkedService;
    }
}
